export default function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto py-8">
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-gray-400 text-sm mb-8">Last updated: February 17, 2026</p>

      <div className="prose prose-invert prose-gray max-w-none space-y-6 text-gray-300 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
          <p>
            MSKAI ("we", "our", "us") operates the Coherence Engine platform, including the mobile
            application, VS Code extension, web dashboard, and related services (collectively, the
            "Services"). This Privacy Policy explains how we collect, use, and protect your
            information when you use our Services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
          <h3 className="text-lg font-medium text-white mb-2">2.1 Account Information</h3>
          <p>
            When you create an account, we collect your name, email address, and authentication
            credentials. We use single sign-on (SSO) for authentication and do not store passwords
            directly.
          </p>

          <h3 className="text-lg font-medium text-white mb-2 mt-4">2.2 Usage Data</h3>
          <p>
            We collect anonymized usage statistics to improve the Services, including feature usage
            frequency, error reports, and performance metrics. This data does not include the content
            of your projects or code.
          </p>

          <h3 className="text-lg font-medium text-white mb-2 mt-4">2.3 Device Information</h3>
          <p>
            For the mobile application, we may collect device type, operating system version, and
            app version to ensure compatibility and deliver updates.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To provide and maintain the Services</li>
            <li>To authenticate your identity and manage your account</li>
            <li>To send you service-related notifications, including updates and security alerts</li>
            <li>To improve and optimize the Services</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">4. Data Storage and Security</h2>
          <p>
            Your data is stored securely using industry-standard encryption. We implement appropriate
            technical and organizational measures to protect your personal data against unauthorized
            access, alteration, disclosure, or destruction.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">5. Third-Party Services</h2>
          <p>
            The Services may integrate with third-party AI providers for processing requests. When
            using cloud AI features, your prompts may be sent to these providers in accordance with
            their respective privacy policies. Local AI processing keeps all data on your
            infrastructure.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
          <p>
            We retain your personal data only for as long as necessary to provide the Services and
            fulfill the purposes described in this policy. You may request deletion of your data at
            any time by contacting us.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Request data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">8. Children's Privacy</h2>
          <p>
            The Services are not intended for use by children under 13 years of age. We do not
            knowingly collect personal information from children.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">10. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or wish to exercise your data rights,
            please contact us at{' '}
            <a href="mailto:privacy@multiskilled.ai" className="text-indigo-400 hover:text-indigo-300">
              privacy@multiskilled.ai
            </a>.
          </p>
        </section>
      </div>
    </div>
  )
}
